/**
 * Frontline-to-rotation conversion for the generation layout.
 *
 * A frontline is a two-point axis drawn on the map (typically the road
 * frontage). The layout x-axis — the direction shelf rows pack along — is set
 * parallel to it, so generation rows run along the frontage exactly like the
 * reference yield tool's "frontline alignment" behaviour.
 *
 * `rotationDeg` follows the layout convention (counter-clockwise from local
 * east, see GenerationLayoutConfiguration.rotationDeg) and reuses the same
 * equirectangular local-plane math as the solver so the drawn line and the
 * packed rows stay visually parallel at any latitude.
 */
import { localAxesForPoint } from './generationLayoutGeometry';

export interface FrontlinePoint {
  lat: number;
  lng: number;
}

/**
 * Rotation (deg CCW from local east) that puts generation rows CROSSWIND to
 * the prevailing wind: the row axis runs perpendicular to the wind's travel
 * direction so upwind exhaust is carried away from the row instead of
 * cascading down the line into the next unit's intake.
 *
 * `prevailingFromDeg` is meteorological (direction the wind comes FROM, deg
 * CW from north). Row axis bearing = FROM + 90; converted to the layout
 * convention that gives rotation = (360 - FROM) mod 360.
 */
export function crosswindRowRotationDeg(prevailingFromDeg: number): number | null {
  if (!Number.isFinite(prevailingFromDeg)) return null;
  const normalized = ((prevailingFromDeg % 360) + 360) % 360;
  const rotation = (360 - normalized) % 360;
  return Math.round(rotation * 10) / 10;
}

/**
 * Rotation (deg CCW from local east, normalized to [0, 360)) that aligns the
 * layout x-axis with the drawn frontline. Returns null for degenerate lines.
 */
export function frontlineRotationDeg(start: FrontlinePoint, end: FrontlinePoint): number | null {
  if (!Number.isFinite(start.lat) || !Number.isFinite(start.lng)
    || !Number.isFinite(end.lat) || !Number.isFinite(end.lng)) {
    return null;
  }
  // rotationDeg 0 -> local axes are plain east/north metres.
  const local = localAxesForPoint(end, start, 0);
  if (local.xM === 0 && local.yM === 0) return null;
  const degrees = Math.atan2(local.yM, local.xM) * 180 / Math.PI;
  const normalized = degrees % 360;
  const rounded = Math.round((normalized < 0 ? normalized + 360 : normalized) * 10) / 10;
  return rounded === 360 ? 0 : rounded;
}
